import Dinero from 'dinero.js';
import { FinancialSummary, LineItem } from './financials';
import { OrderType } from '../order/order';
import { TaxBreakdownJson, toTaxBreakdown } from '../tax/tax';

export namespace FinancialsParsers {
  export class FinancialSummaryJson {
    lineItems!: LineItemJson[];
    total!: Dinero.Options;
    googleCalculation?: OrderGoogleCalculationJson;
    error?: string;
  }

  class OrderGoogleCalculationJson {
    type!: 'OrderGoogleCalculation';
    calculationType!: 'Distance' | 'Duration';
    distance!: { value: number; text: string };
    duration!: { value: number; text: string };
  }
  class BaseLineItemJson {
    description!: string;
    unitPrice!: Dinero.Options;
    quantity!: number;
    amount!: Dinero.Options;
    taxable!: boolean;
  }

  class PercentOrFixedLineItemJson extends BaseLineItemJson {
    percentage?: number; // 0-100
  }

  class CustomizableLineItemJson extends BaseLineItemJson {
    hasCustomAmount!: boolean;
  }

  class CustomizablePercentOrFixedLineItemJson extends PercentOrFixedLineItemJson {
    hasCustomAmount!: boolean;
  }

  class ProductLineItemJson extends CustomizableLineItemJson {
    id!: string;
    type!: 'Product';
    productId!: number;
    transactionType!: OrderType;
  }

  class ProductFeeLineItemJson extends CustomizablePercentOrFixedLineItemJson {
    type!: 'ProductFee';
    productId!: number;
    productLineItemId!: string;
    productFeeId!: number;
    optional!: boolean;
    isSelected!: boolean;
    recurring!: boolean;
  }

  class ProductSecurityDepositLineItemJson extends BaseLineItemJson {
    type!: 'ProductSecurityDeposit';
    productId!: number;
    productLineItemId!: string;
  }

  class FeeLineItemJson extends CustomizablePercentOrFixedLineItemJson {
    type!: 'Fee';
    feeId!: number;
    optional!: boolean;
    isSelected!: boolean;
  }

  class DamageWaiverLineItemJson extends CustomizablePercentOrFixedLineItemJson {
    type!: 'DamageWaiver';
    optional!: boolean;
    isSelected!: boolean;
  }

  class DiscountLineItemJson extends PercentOrFixedLineItemJson {
    type!: 'AdditionalDiscount' | 'Coupon' | 'TagPricing' | 'ProductDiscount';
  }

  class ProductDiscountLineItemJson extends DiscountLineItemJson {
    type!: 'ProductDiscount';
    productId!: number;
  }

  class CouponLineItemJson extends DiscountLineItemJson {
    type!: 'Coupon' | 'TagPricing';
    code!: string;
    productIds?: number[];
    maxValue?: Dinero.Dinero;
    isVip!: boolean;
  }

  class ProductDeliveryLineItemJson extends BaseLineItemJson {
    type!: 'ProductDelivery';
    productId!: number;
    productLineItemId!: string;
  }

  class DeliveryLineItemJson extends CustomizableLineItemJson {
    type!: 'Delivery';
  }

  class ProductPickupLineItemJson extends BaseLineItemJson {
    type!: 'ProductDelivery';
    productId!: number;
    productLineItemId!: string;
  }

  class PickupLineItemJson extends CustomizableLineItemJson {
    type!: 'Delivery';
  }

  class TaxLineItemJson extends CustomizableLineItemJson {
    type!: 'Tax';
    percentage!: number;
    hasCustomPercentage!: boolean;
    userIsExempted!: boolean;
    taxBreakdown: TaxBreakdownJson[] | undefined;
    taxCodeId: string | undefined;
  }

  class SecurityDepositLineItemJson extends BaseLineItemJson {
    type!: 'SecurityDeposit';
    percentage!: number;
  }

  export type LineItemJson =
    | ProductLineItemJson
    | ProductFeeLineItemJson
    | FeeLineItemJson
    | DamageWaiverLineItemJson
    | DiscountLineItemJson
    | ProductDiscountLineItemJson
    | CouponLineItemJson
    | ProductDeliveryLineItemJson
    | DeliveryLineItemJson
    | ProductPickupLineItemJson
    | PickupLineItemJson
    | ProductSecurityDepositLineItemJson
    | SecurityDepositLineItemJson
    | TaxLineItemJson;

  export function toFinancialSummary(input: FinancialSummaryJson): FinancialSummary {
    return {
      lineItems: input.lineItems.map((li) => toLineItem(li)),
      total: Dinero(input.total),
      googleCalculation: input.googleCalculation,
      error: input.error,
    };
  }

  export function toLineItem(input: LineItemJson): LineItem {
    return {
      ...input,
      unitPrice: Dinero(input.unitPrice),
      amount: Dinero(input.amount),
      taxBreakdown: input.type == 'Tax' && input.taxBreakdown ? input.taxBreakdown.map(toTaxBreakdown) : undefined,
    };
  }
}
